import isFunction from 'lodash/isFunction';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import flow from 'lodash/flow';

import { showPairingSuccessPageCookie } from '../../constants/cookies';
import { grantDeviceAuth } from '../../actions/auth';
import {
  getUserCodeParam,
  hasPairAuthFlowParam,
} from '../../utils/auth/pairAuthFlowHelpers';
import { setCookie } from '../../utils/cookie';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import connectWithAuth from './connectWithAuth';

export default function withAuthHandling(handlers = {}, options = {}) {
  const { authRedirectHandler, unauthRedirectHandler } = handlers;
  const { isPartner } = options;

  return (ProtectedComponent) => {
    class WithAuthHandling extends Component {
      static propTypes = {
        isAuthenticated: PropTypes.bool.isRequired,
        history: PropTypes.object.isRequired,
        userDetails: PropTypes.object,
        actions: PropTypes.shape({
          grantDeviceAuth: PropTypes.func,
        }),
      };

      static contextType = LocationAndLocalizationContext;

      async componentDidUpdate(prevProps) {
        const { actions, isAuthenticated, userDetails } = this.props;
        const { isAuthenticated: wasAuthenticated } = prevProps;
        const { grantDeviceAuth: grantDeviceAuthAction } = actions;
        const { location } = this.context;
        const { query } = location;

        const isPairingFlow = isPartner && hasPairAuthFlowParam(query);
        const userCode = getUserCodeParam(query);

        if (isAuthenticated && !wasAuthenticated) {
          if (
            isPairingFlow &&
            !!userCode &&
            isFunction(grantDeviceAuthAction)
          ) {
            setCookie(showPairingSuccessPageCookie.name, 1);
            const deviceAuthResponse = await grantDeviceAuthAction(userCode);

            if (deviceAuthResponse?.value?.status !== 200) {
              // TODO: handle failed grantDeviceAuth request to platform in the UI
              return;
            }
          }

          this.conditionallyReplaceRouteWithDescriptor(
            authRedirectHandler,
            true,
            location,
            userDetails,
          );
        } else if (!isAuthenticated && wasAuthenticated) {
          // Loses authentication
          this.conditionallyReplaceRouteWithDescriptor(
            unauthRedirectHandler,
            false,
            location,
          );
        }
      }

      /**
       *
       * @param {function} handler - Function to get the un/authenticated location descriptor
       * @param {boolean} authFlag - Is the user authenticated?
       * @param {string} location - Location of user
       * @param {object} user - User details
       */
      conditionallyReplaceRouteWithDescriptor = (
        handler,
        authFlag,
        location = null,
        user = null,
      ) => {
        let locationDescriptor;

        if (isFunction(handler)) {
          locationDescriptor = handler(location, user);
          if (locationDescriptor) {
            this.replaceRoute(true, locationDescriptor);
          }
        } else {
          // Refresh current route
          this.replaceRoute(authFlag);
        }
      };

      replaceRoute(authenticated, locationDescriptor) {
        const location = locationDescriptor || window.location;
        this.props.history.replace({
          ...location,
          // NOTE: Needed for the reloadOnPropsChange check in `client/Root.js`
          state: {
            authenticated,
          },
        });
      }

      render() {
        /* eslint-disable no-unused-vars */
        const { isAuthenticated, ...otherProps } = this.props;

        return <ProtectedComponent {...otherProps} />;
      }
    }

    function mapDispatchToProps(dispatch) {
      return {
        actions: bindActionCreators(
          {
            grantDeviceAuth,
          },
          dispatch,
        ),
      };
    }

    return flow(
      connect(null, mapDispatchToProps),
      connectWithAuth,
      withRouter,
    )(WithAuthHandling);
  };
}
